import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type DocumentTemplatesResult,
  type PreviewQuoteDocumentResult,
  type QuoteDocumentsResult,
} from '@whinerp/client';
import { Button, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT, useErrorMessage } from '../../app/i18n.js';

/**
 * Printing this quote from a template (F-050).
 *
 * The UX pointer is the design: "show merge-field validation before send. A
 * quote that goes out containing an unresolved placeholder is a customer-facing
 * error." So choosing a template shows, before anything is generated, the three
 * things that would stop it - each in words that say who can fix it - and the
 * Generate button does nothing until all three are clear.
 */
export function QuoteDocumentsPanel({
  quoteId,
  defaultTemplateId = null,
}: {
  quoteId: string;
  /** The quote template's choice of branding (F-158). Only the starting selection. */
  defaultTemplateId?: string | null;
}): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [chosen, setTemplateId] = useState<string | null>(null);

  const templates = useQuery<DocumentTemplatesResult, ApiError>({
    queryKey: ['document-templates', 'quote'],
    queryFn: () => api.documentTemplates({ query: { objectKey: 'quote' } }),
  });

  // The quote template's choice, until somebody picks - and only while that
  // document template still exists, so the select never shows one that is gone.
  const offered = templates.data?.templates.some((entry) => entry.templateId === defaultTemplateId) ?? false;
  const templateId = chosen ?? (offered && defaultTemplateId ? defaultTemplateId : '');

  const history = useQuery<QuoteDocumentsResult, ApiError>({
    queryKey: ['quote-documents', quoteId],
    queryFn: () => api.quoteDocuments({ path: { quoteId } }),
  });

  const preview = useQuery<PreviewQuoteDocumentResult, ApiError>({
    queryKey: ['quote-document-preview', quoteId, templateId],
    queryFn: () => api.previewQuoteDocument({ path: { quoteId }, query: { templateId } }),
    enabled: templateId !== '',
  });

  const generate = useMutation<unknown, ApiError>({
    mutationFn: () => api.generateQuoteDocument({ path: { quoteId }, body: { templateId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quote-documents', quoteId] });
    },
  });

  if (templates.isPending || history.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (templates.isError) {
    return <ErrorState title={t('state.error')} body={errorText(templates.error)} onRetry={() => void templates.refetch()} />;
  }
  if (history.isError) {
    return <ErrorState title={t('state.error')} body={errorText(history.error)} onRetry={() => void history.refetch()} />;
  }

  const ready = preview.data?.ready === true;

  return (
    <Panel data-region="quote-documents">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('doc.generateTitle')}</SectionLabel>

        {templates.data.templates.length === 0 ? (
          <p data-doc-no-templates className="text-meta text-ink-meta">
            {t('doc.noTemplates')}
          </p>
        ) : (
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('doc.template')}
            <select
              data-doc-choose
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
              value={templateId}
              onChange={(event) => {
                generate.reset();
                setTemplateId(event.target.value);
              }}
            >
              <option value="">{t('doc.choose')}</option>
              {templates.data.templates.map((template) => (
                <option key={template.templateId} value={template.templateId}>
                  {template.name} ({template.format.toUpperCase()})
                </option>
              ))}
            </select>
          </label>
        )}

        {preview.isError ? (
          <p data-doc-preview-error className="text-dense text-danger-text">
            {errorText(preview.error)}
          </p>
        ) : null}

        {preview.data ? (
          <div data-doc-preview data-doc-ready={ready ? 'yes' : 'no'} className="flex flex-col gap-1">
            {/* Three lists, three different people who can fix them. */}
            {preview.data.unknownFields.length > 0 ? (
              <p data-doc-unknown className="text-dense text-danger-text">
                {t('doc.unknownFields', { values: { fields: preview.data.unknownFields.join(', ') } })}
              </p>
            ) : null}
            {preview.data.emptyFields.length > 0 ? (
              <p data-doc-empty className="text-dense text-danger-text">
                {t('doc.emptyFields', { values: { fields: preview.data.emptyFields.join(', ') } })}
              </p>
            ) : null}
            {preview.data.unsettable.length > 0 ? (
              <p data-doc-unsettable className="text-dense text-danger-text">
                {t('doc.unsettable', { values: { characters: preview.data.unsettable.join(' ') } })}
              </p>
            ) : null}
            {ready ? (
              <p data-doc-ready-note className="text-dense text-ink-600">
                {t('doc.ready')}
              </p>
            ) : null}
          </div>
        ) : null}

        {generate.error ? (
          <p data-doc-generate-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {errorText(generate.error)}
          </p>
        ) : null}

        <div>
          <Button
            intent="secondary"
            size="small"
            data-doc-generate
            disabled={!ready}
            onClick={() => generate.mutate()}
          >
            {t('doc.generate')}
          </Button>
        </div>

        {history.data.documents.length > 0 ? (
          <ul className="flex flex-col gap-0.5">
            {history.data.documents.map((entry) => (
              <li key={entry.documentId} data-doc-generated={entry.documentId} className="text-meta text-ink-600">
                {entry.templateName} · {entry.format.toUpperCase()} · {formatDateTime(entry.generatedAt, format)}
                {entry.generatedBy ? ` · ${entry.generatedBy}` : ''}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
